import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/hooks/useAuth';
import {
  AdminTable, AdminSectionHeader, StatusBadge,
  inputCls, labelCls, tdCls, exportCsv, InlineError,
} from '@/components/admin/AdminShared';

type NamedRow = { id: number; name: string };

/**
 * Normalise whatever the DB / driver hands us into a "YYYY-MM-DD" string.
 * Drizzle's MySQL `date` column may return either a string or a Date depending
 * on the driver version — both inputs become a valid `<input type="date">` value.
 */
function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  // Date object — use local-time components so we don't slip a day across timezones
  const y = value.getFullYear();
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Normalise time to "HH:MM" regardless of the driver returning "HH:MM:SS". */
function toTimeInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 5);
}

// ─── Edit modal ───────────────────────────────────────────────────────────────
function EditModal({
  session,
  instructors,
  isAdmin,
  onClose,
  onSaved,
}: {
  session: {
    id: number; date: string | Date; time: string;
    capacity: number; price: string | number; instructorId: number | null;
  };
  instructors: NamedRow[];
  isAdmin:     boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const utils = trpc.useUtils();
  const [form, setForm] = useState({
    date:         toDateInputValue(session.date),
    time:         toTimeInputValue(session.time),
    capacity:     session.capacity,
    price:        String(session.price ?? '0'),
    instructorId: session.instructorId != null ? String(session.instructorId) : '',
  });

  const update = trpc.sessions.update.useMutation({
    onSuccess: async () => {
      // Refresh both the admin's listAll and the public list used by the booking page
      await Promise.all([
        utils.sessions.listAll.invalidate(),
        utils.sessions.list.invalidate(),
      ]);
      onSaved();
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50
                 flex items-center justify-center p-4"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#09090B] border border-[#27272A] rounded-3xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-black text-white">Edit Session</h3>
          <button onClick={onClose}
                  className="text-[#52525B] hover:text-white text-xl leading-none">✕</button>
        </div>

        <form
          onSubmit={e => {
            e.preventDefault();
            const priceNum = parseFloat(form.price);
            if (Number.isNaN(priceNum) || priceNum < 0) {
              alert('Enter a valid price (0 or greater).');
              return;
            }
            update.mutate({
              id:       session.id,
              date:     form.date,
              time:     form.time,
              capacity: form.capacity,
              price:    priceNum,
              ...(isAdmin && { instructorId: form.instructorId ? Number(form.instructorId) : null }),
            });
          }}
          className="space-y-4"
        >
          <div>
            <label className={labelCls}>Date</label>
            <input type="date" required value={form.date}
                   onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                   className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Time</label>
            <input type="time" required value={form.time}
                   onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                   className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Capacity</label>
            <input type="number" min={1} required value={form.capacity}
                   onChange={e => setForm(f => ({ ...f, capacity: +e.target.value }))}
                   className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Price (KD)</label>
            <input type="number" min={0} step={0.01} required value={form.price}
                   onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                   className={inputCls} placeholder="0 = free" />
          </div>
          {isAdmin && (
            <div>
              <label className={labelCls}>Instructor</label>
              <select value={form.instructorId}
                      onChange={e => setForm(f => ({ ...f, instructorId: e.target.value }))}
                      className={inputCls}>
                <option value="">— None —</option>
                {instructors.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
              </select>
            </div>
          )}

          {update.error && <InlineError message={update.error.message} />}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
                    className="flex-1 py-3 border border-white/10 text-[#A1A1AA]
                               hover:text-white rounded-2xl text-sm font-medium transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={update.isPending}
                    className="flex-1 py-3 bg-yellow-400 hover:bg-yellow-400
                               text-black font-black rounded-2xl text-sm disabled:opacity-50">
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AdminSessions() {
  const utils = trpc.useUtils();
  const { isAdmin } = useAuth();
  const { data: sessions = [], refetch } = trpc.sessions.listAll.useQuery();
  const { data: instructors = [] }       = trpc.instructors.list.useQuery();

  /** Refresh both the admin and the public (booking page) caches. */
  const refreshAll = async () => {
    await Promise.all([
      utils.sessions.listAll.invalidate(),
      utils.sessions.list.invalidate(),
    ]);
  };

  const createSession = trpc.sessions.create.useMutation({
    onSuccess: async () => {
      await refreshAll();
      setForm({ date: '', time: '', capacity: 1, price: '0', instructorId: '' });
      setShowAdd(false);
    },
  });
  const deleteSession = trpc.sessions.delete.useMutation({
    onSuccess: () => refreshAll(),
  });

  const [showAdd,    setShowAdd]    = useState(false);
  const [editTarget, setEditTarget] = useState<(typeof sessions)[number] | null>(null);
  const [deleteId,   setDeleteId]   = useState<number | null>(null);
  const [form, setForm] = useState({ date: '', time: '', capacity: 1, price: '0', instructorId: '' });

  // ── Filters ────────────────────────────────────────────────────────────
  const [fDate,       setFDate]       = useState('');
  const [fInstructor, setFInstructor] = useState('');
  const [fCapacity,   setFCapacity]   = useState('');
  const [fTime,       setFTime]       = useState('');

  const filtered = sessions.filter(s => {
    if (fDate && toDateInputValue(s.date) !== fDate) return false;
    if (fInstructor && String((s as any).instructorId ?? '') !== fInstructor) return false;
    if (fCapacity && s.capacity < Number(fCapacity)) return false;
    if (fTime && toTimeInputValue(s.time) !== fTime) return false;
    return true;
  });
  const filtersActive = !!(fDate || fInstructor || fCapacity || fTime);

  const handleExport = () =>
    exportCsv(
      sessions.map(s => ({
        ID:              s.id,
        Date:            s.date,
        Time:            s.time,
        Capacity:        s.capacity,
        Available_Slots: s.availableSlots,
        Price_KD:        s.price,
        Bookings:        s.bookings.length,
      })),
      'mrpanadol_sessions'
    );

  return (
    <div>
      <AdminSectionHeader title="Sessions" count={sessions.length} onExport={handleExport} />

      {/* Add toggle */}
      <div className="mb-6">
        <button
          onClick={() => setShowAdd(v => !v)}
          className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-400
                     text-black font-bold text-sm px-5 py-2.5 rounded-xl transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24"
               stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {showAdd ? 'Cancel' : 'Add Session'}
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="bg-[#09090B] border border-yellow-400/20 border-dashed
                        rounded-3xl p-6 mb-8">
          <h3 className="text-base font-black text-white mb-5">New Session</h3>
          <form
            onSubmit={e => {
              e.preventDefault();
              const priceNum = parseFloat(form.price);
              if (Number.isNaN(priceNum) || priceNum < 0) {
                alert('Enter a valid price (0 or greater).');
                return;
              }
              createSession.mutate({
                date:     form.date,
                time:     form.time,
                capacity: form.capacity,
                price:    priceNum,
                ...(isAdmin && { instructorId: form.instructorId ? Number(form.instructorId) : null }),
              });
            }}
            className="space-y-4"
          >
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>Date *</label>
                <input type="date" required value={form.date}
                       onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                       className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Time *</label>
                <input type="time" required value={form.time}
                       onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                       className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Capacity *</label>
                <input type="number" min={1} required value={form.capacity}
                       onChange={e => setForm(f => ({ ...f, capacity: +e.target.value }))}
                       className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Price (KD) *</label>
                <input type="number" min={0} step={0.01} required value={form.price}
                       onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                       placeholder="0 = free" className={inputCls} />
              </div>
              {isAdmin && (
                <div>
                  <label className={labelCls}>Instructor</label>
                  <select value={form.instructorId}
                          onChange={e => setForm(f => ({ ...f, instructorId: e.target.value }))}
                          className={inputCls}>
                    <option value="">— None —</option>
                    {instructors.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
                  </select>
                </div>
              )}
            </div>

            {createSession.error && <InlineError message={createSession.error.message} />}

            <button type="submit" disabled={createSession.isPending}
                    className="bg-yellow-400 hover:bg-yellow-400 text-black font-black
                               px-6 py-2.5 rounded-xl text-sm disabled:opacity-50">
              {createSession.isPending ? 'Creating…' : 'Create Session'}
            </button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div>
          <label className={labelCls}>Day</label>
          <input type="date" value={fDate} onChange={e => setFDate(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Time</label>
          <input type="time" value={fTime} onChange={e => setFTime(e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Min capacity</label>
          <input type="number" min={1} value={fCapacity} onChange={e => setFCapacity(e.target.value)}
                 placeholder="any" className={inputCls} />
        </div>
        {isAdmin && instructors.length > 0 && (
          <div>
            <label className={labelCls}>Instructor</label>
            <select value={fInstructor} onChange={e => setFInstructor(e.target.value)} className={inputCls}>
              <option value="">All</option>
              {instructors.map(i => <option key={i.id} value={String(i.id)}>{i.name}</option>)}
            </select>
          </div>
        )}
        {filtersActive && (
          <button
            onClick={() => { setFDate(''); setFTime(''); setFCapacity(''); setFInstructor(''); }}
            className="text-xs text-[#A1A1AA] hover:text-white border border-white/10
                       hover:border-white/20 px-3 py-2 rounded-lg transition-colors">
            Clear filters
          </button>
        )}
      </div>

      {/* Sessions table */}
      <AdminTable
        headers={['Date', 'Time', 'Capacity', 'Available', 'Price', 'Bookings', 'Status', 'Actions']}
        empty={filtered.length === 0}
      >
        {filtered.map(s => (
          <tr key={s.id} className="border-t border-white/[0.04]">
            <td className={`${tdCls} text-white font-semibold whitespace-nowrap`}>{s.date}</td>
            <td className={`${tdCls} text-[#E4E4E7] whitespace-nowrap`}>{s.time.slice(0, 5)}</td>
            <td className={`${tdCls} text-[#A1A1AA]`}>{s.capacity}</td>
            <td className={`${tdCls} text-[#A1A1AA]`}>{s.availableSlots}</td>
            <td className={`${tdCls} text-yellow-400 font-bold whitespace-nowrap`}>
              {parseFloat((s as any).price as string) === 0 ? 'Free' : `${(s as any).price} KD`}
            </td>
            <td className={`${tdCls} text-[#A1A1AA]`}>
              {s.bookings.length > 0 ? (
                <div className="space-y-0.5">
                  {s.bookings.map(b => (
                    <div key={b.id} className="flex items-center gap-1.5">
                      <span className="text-[#A1A1AA] text-xs">{(b as any).user?.name ?? '—'}</span>
                      <StatusBadge status={b.status} />
                    </div>
                  ))}
                </div>
              ) : (
                <span className="text-[#3F3F46] text-xs">none</span>
              )}
            </td>
            <td className={tdCls}>
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${
                s.availableSlots > 0
                  ? 'bg-green-500/10 text-green-400'
                  : 'bg-[#27272A]/60 text-[#71717A]'
              }`}>
                {s.availableSlots > 0 ? 'Open' : 'Full'}
              </span>
            </td>
            <td className={tdCls}>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditTarget(s)}
                  className="text-xs text-[#A1A1AA] hover:text-white border border-white/10
                             hover:border-white/20 px-3 py-1.5 rounded-lg transition-colors"
                >
                  Edit
                </button>
                {deleteId === s.id ? (
                  <div className="flex gap-1.5 items-center">
                    <span className="text-[#71717A] text-xs">Sure?</span>
                    <button
                      onClick={() => { deleteSession.mutate({ id: s.id }); setDeleteId(null); }}
                      className="text-xs text-red-400 border border-red-400/30 px-2.5 py-1
                                 rounded-lg hover:bg-red-400/10 transition-colors"
                    >Yes</button>
                    <button onClick={() => setDeleteId(null)}
                            className="text-xs text-[#71717A] border border-white/10 px-2.5 py-1
                                       rounded-lg transition-colors">No</button>
                  </div>
                ) : (
                  <button
                    onClick={() => setDeleteId(s.id)}
                    disabled={s.bookings.length > 0}
                    title={s.bookings.length > 0 ? 'Cancel bookings first' : 'Delete session'}
                    className="text-xs text-red-400 border border-red-400/20
                               hover:border-red-400/40 px-3 py-1.5 rounded-lg transition-colors
                               disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    Delete
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </AdminTable>

      {editTarget && (
        <EditModal
          instructors={instructors}
          isAdmin={isAdmin}
          session={{
            id:           editTarget.id,
            date:         editTarget.date,
            time:         editTarget.time,
            capacity:     editTarget.capacity,
            price:        (editTarget as any).price ?? '0',
            instructorId: (editTarget as any).instructorId ?? null,
          }}
          onClose={() => setEditTarget(null)}
          onSaved={() => refetch()}
        />
      )}
    </div>
  );
}
